import { api, newId } from '@stageplottter/core';
import { ApiError, ContractError, NetworkError, REQUEST_ID_HEADER } from './client';
import type { Schema } from './client';

interface UploadOptions {
  onProgress?(loaded: number, total: number): void;
  baseUrl?: string;
}

/**
 * Uploads a portable board file (U7) as the raw request body – files can be large, so it is sent
 * as is (no re-encoding) with XMLHttpRequest for upload progress.
 */
function postBoardFile<T>(
  path: string,
  body: Blob,
  schema: Schema<T>,
  opts: UploadOptions,
): Promise<T> {
  const requestId = newId();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${opts.baseUrl ?? '/api/v1'}${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader(REQUEST_ID_HEADER, requestId);
    xhr.upload.onprogress = (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : 0);
    xhr.onerror = () => reject(new NetworkError(path, new Error('request failed')));
    xhr.onload = () => {
      let json: unknown = null;
      try {
        json = JSON.parse(xhr.responseText);
      } catch {
        json = null;
      }
      if (xhr.status >= 400) {
        const e = api.ErrorResponse.safeParse(json);
        const rid = xhr.getResponseHeader(REQUEST_ID_HEADER) ?? requestId;
        reject(
          e.success
            ? new ApiError(xhr.status, e.data.error, e.data.message, rid, e.data.details)
            : new ApiError(xhr.status, 'http_error', xhr.statusText, rid),
        );
        return;
      }
      const parsed = schema.safeParse(json);
      if (parsed.success) resolve(parsed.data);
      else reject(new ContractError(path, parsed.error.message));
    };
    xhr.send(body);
  });
}

/** "Import board…": the file becomes a new board of the workspace (U7). */
export function importBoardFile(
  workspaceId: string,
  body: Blob,
  opts: UploadOptions & { name?: string } = {},
): Promise<api.BoardSummary> {
  const q = opts.name ? `?name=${encodeURIComponent(opts.name)}` : '';
  return postBoardFile(
    `/workspaces/${encodeURIComponent(workspaceId)}/boards/import${q}`,
    body,
    api.BoardSummary,
    opts,
  );
}

/** "Replace from file…": the file replaces the content of an existing board (U93). */
export function replaceBoardFile(
  boardId: string,
  body: Blob,
  opts: UploadOptions & { snapshotName?: string } = {},
): Promise<api.DocumentResponse> {
  const q = opts.snapshotName ? `?snapshotName=${encodeURIComponent(opts.snapshotName)}` : '';
  return postBoardFile(
    `/boards/${encodeURIComponent(boardId)}/replace${q}`,
    body,
    api.DocumentResponse,
    opts,
  );
}
